import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, BookOpen, MusicNote, Notebook, FileText, SignOut, DotsThree, TrashSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { projectApi } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Brand, ThemeToggle } from "@/components/Brand";

const ICONS = { book: BookOpen, music: MusicNote, notebook: Notebook, file: FileText };

export default function Home() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("book");
  const [genre, setGenre] = useState("Roman");
  const [withDemo, setWithDemo] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    projectApi.list().then(setProjects).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Donnez un nom au projet");
    setCreating(true);
    try {
      const proj = await projectApi.create({ name, icon, genre });
      if (withDemo) {
        toast.info("Import du manuscrit de démonstration…");
        await projectApi.demo(proj.id, 12);
      }
      toast.success("Projet créé");
      setOpen(false);
      setName("");
      setWithDemo(false);
      navigate(`/project/${proj.id}`);
    } catch (e) {
      toast.error("Impossible de créer le projet");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement ce projet ?")) return;
    await projectApi.remove(id);
    toast.success("Projet supprimé");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <button data-testid="logout-button" onClick={logout} className="h-9 w-9 grid place-items-center rounded-sm border border-border bg-surface hover:bg-surface-hover transition-colors" title="Se déconnecter">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="overline text-primary mb-2">Espace de travail GABRYX</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight">Mes projets</h1>
          </div>
          <Button data-testid="new-project-button" onClick={() => setOpen(true)} className="rounded-sm h-11 gap-2">
            <Plus size={18} weight="bold" /> Nouveau projet
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg py-24 text-center">
            <BookOpen size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <p className="font-serif text-2xl mb-2">Aucun manuscrit pour l'instant</p>
            <p className="text-muted-foreground mb-6">Créez un projet et importez votre premier manuscrit.</p>
            <Button onClick={() => setOpen(true)} className="rounded-sm gap-2"><Plus size={18} /> Nouveau projet</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {projects.map((p, i) => {
              const Icon = ICONS[p.icon] || BookOpen;
              const pct = p.chapter_count ? Math.round((p.analyzed_chapters / p.chapter_count) * 100) : 0;
              return (
                <motion.div
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => navigate(`/project/${p.id}`)}
                  className="group relative cursor-pointer border border-border bg-surface rounded-lg aspect-[3/4] flex flex-col justify-between p-6 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] overflow-hidden"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 grid place-items-center rounded-sm bg-primary/10 text-primary">
                      <Icon size={22} weight="duotone" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button className="h-8 w-8 grid place-items-center rounded-sm hover:bg-surface-hover opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`project-menu-${p.id}`}>
                          <DotsThree size={20} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover">
                        <DropdownMenuItem className="text-destructive" onClick={(e) => remove(p.id, e)}>
                          <TrashSimple size={16} className="mr-2" /> Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div>
                    <p className="overline text-muted-foreground mb-1">{p.genre}</p>
                    <h3 className="font-serif text-xl leading-tight mb-3 line-clamp-3">{p.name}</h3>
                    <div className="text-xs text-muted-foreground font-mono mb-2 flex gap-3">
                      <span>{p.chapter_count} chap.</span>
                      <span>{(p.word_count || 0).toLocaleString("fr")} mots</span>
                    </div>
                    <Progress value={pct} className="h-1" />
                    <p className="text-xs text-muted-foreground mt-1.5">{pct}% analysé</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover" data-testid="new-project-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Nouveau projet</DialogTitle>
            <DialogDescription>Choisissez un titre, un type et un genre pour votre manuscrit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Titre du manuscrit</Label>
              <Input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Les Ombres de la Rue Rouge" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={icon} onValueChange={setIcon}>
                  <SelectTrigger className="mt-1" data-testid="project-icon-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="book">📖 Roman</SelectItem>
                    <SelectItem value="music">🎵 Paroles</SelectItem>
                    <SelectItem value="notebook">📕 Nouveau roman</SelectItem>
                    <SelectItem value="file">📝 Nouvelles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Genre</Label>
                <Input value={genre} onChange={(e) => setGenre(e.target.value)} className="mt-1" />
              </div>
            </div>
            <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-surface-hover transition-colors">
              <input type="checkbox" data-testid="project-demo-checkbox" checked={withDemo} onChange={(e) => setWithDemo(e.target.checked)} className="mt-1 accent-primary" />
              <div>
                <p className="text-sm font-medium">Charger un manuscrit de démonstration</p>
                <p className="text-xs text-muted-foreground">« Les Ombres de la Rue Rouge » — 12 chapitres pour tester tout le parcours.</p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="confirm-create-project" onClick={create} disabled={creating} className="rounded-sm">
              {creating ? "Création…" : "Créer le projet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
